import { z } from "zod";

const id = z.string().min(1).max(100);
const optionalId = z
  .union([id, z.literal(""), z.null()])
  .optional()
  .transform((value) => (value ? value : null));
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式应为 yyyy-mm-dd")
  .optional()
  .nullable();

export const assetInstanceDetailSchema = z.object({
  serialNumber: z.string().trim().max(120).optional().nullable(),
  financialCardNo: z.string().trim().max(120).optional().nullable(),
  minorIssues: z.array(z.string()).optional().default([]),
});

export const assetInboundSchema = z.object({
  quantity: z.coerce.number().int().min(1).max(100),
  purchaseUnitPriceYuan: z.coerce.number().min(0).max(100_000_000),
  inboundDate: dateString,
  warrantyExpiry: dateString,
  purchaseOrderNo: z.string().trim().max(100).optional().nullable(),
  contractNo: z.string().trim().max(100).optional().nullable(),
  brandOptionId: optionalId,
  modelSpec: z.string().trim().max(200).optional().nullable(),
  costCenterId: optionalId,
  locationId: optionalId,
  location: z.string().trim().max(200).optional().nullable(),
  assetManagerId: optionalId,
  condition: z.enum(["new", "used"]).default("new"),
  acceptanceRequired: z.boolean().default(true),
  instanceDetails: z.array(assetInstanceDetailSchema).max(100).optional().default([]),
});

export const assetUpdateSchema = z.object({
  serialNumber: z.string().trim().max(120).optional().nullable(),
  financialCardNo: z.string().trim().max(120).optional().nullable(),
  inboundDate: dateString,
  warrantyExpiry: dateString,
  purchaseOrderNo: z.string().trim().max(100).optional().nullable(),
  contractNo: z.string().trim().max(100).optional().nullable(),
  brandOptionId: optionalId,
  modelSpec: z.string().trim().max(200).optional().nullable(),
  costCenterId: optionalId,
  locationId: optionalId,
  location: z.string().trim().max(200).optional().nullable(),
  assetManagerId: optionalId,
  condition: z.enum(["new", "used"]).optional(),
});

export const assetTransitionSchema = z.object({
  action: z.enum([
    "accept",
    "assign",
    "borrow",
    "return",
    "complete_return",
    "start_repair",
    "finish_repair",
    "retire",
    "dispose",
    "report_lost",
    "recover_lost",
  ]),
  holderId: optionalId,
  confirmationText: z.string().trim().max(500).optional().nullable(),
  hostname: z.string().trim().max(120).optional().nullable(),
  operatingSystemOptionId: optionalId,
  officeSoftwareOptionId: optionalId,
  wifiMacAddress: z.string().trim().max(64).optional().nullable(),
  instrumentConnected: z.boolean().optional(),
  connectedInstrumentNo: z.string().trim().max(120).optional().nullable(),
  disposalMethod: z.enum(["sold", "recycled", "scrapped", "other"]).optional().nullable(),
  disposalNote: z.string().trim().max(500).optional().nullable(),
  dataCleared: z.boolean().optional(),
});

export const accessoryInboundSchema = z.object({
  materialId: id,
  quantity: z.coerce.number().int().min(1).max(200),
  purchaseUnitPriceYuan: z.coerce.number().min(0).max(10_000_000),
  inboundDate: dateString,
  compatibleModel: z.string().trim().max(200).optional().nullable(),
  location: z.string().trim().max(200).optional().nullable(),
  financialCardNo: z.string().trim().max(120).optional().nullable(),
  instanceDetails: z
    .array(
      z.object({
        financialCardNo: z.string().trim().max(120).optional().nullable(),
        minorIssues: z.array(z.string()).optional().default([]),
      }),
    )
    .max(200)
    .optional()
    .default([]),
});

export const accessoryAssignSchema = z.object({
  targetUserId: id,
  assetId: optionalId,
  location: z.string().trim().min(1, "请填写使用位置").max(200),
});

export const accessoryReturnSchema = z.object({
  usable: z.boolean(),
  reason: z.string().trim().max(500).optional().nullable(),
});

export const accessoryScrapSchema = z.object({
  reason: z.string().trim().min(1, "请填写报废原因").max(500),
});

export const consumableInboundSchema = z.object({
  materialId: id,
  batchDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "请选择批次日期"),
  quantity: z.coerce.number().int().min(1).max(1_000_000),
  purchaseUnitPriceYuan: z.coerce.number().min(0).max(10_000_000),
  supplier: z.string().trim().max(200).optional().nullable(),
  expiryDate: dateString,
});

export const consumableIssueSchema = z.object({
  materialId: id,
  quantity: z.coerce.number().int().min(1).max(1_000_000),
  companyId: optionalId,
  departmentId: optionalId,
  purpose: z.string().trim().max(500).optional().nullable(),
});

export const licenseCreateSchema = z.object({
  materialId: id,
  mode: z.enum(["subscription", "term", "perpetual"]),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  expiryDate: dateString,
  purchaseUnitPriceYuan: z.coerce.number().min(0).max(100_000_000),
  totalSeats: z.coerce.number().int().min(1).max(1000),
  vendor: z.string().trim().max(200).optional().nullable(),
  softwareVersion: z.string().trim().max(120).optional().nullable(),
  entitlementScope: z.string().trim().max(200).optional().nullable(),
  supportExpiryDate: dateString,
  billingCycleMonths: z.coerce.number().int().min(1).max(120).default(1),
  licenseKeyRef: z.string().trim().max(200).optional().nullable(),
});

export const seatAllocateSchema = z.object({
  userId: optionalId,
  assetId: optionalId,
  durationMonths: z.coerce.number().int().min(1).max(120).optional().nullable(),
  confirmationText: z.string().trim().max(500).optional().nullable(),
});

export const labelTaskCreateSchema = z.object({
  targetType: z.enum(["asset", "accessory"]),
  targetId: id,
  quantity: z.coerce.number().int().min(1).max(20).default(1),
  businessSource: z.string().trim().max(40).default("manual"),
});

export const licenseRenewSchema = z.object({
  expiryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  purchaseUnitPriceYuan: z.coerce.number().min(0).max(100_000_000).optional(),
});

export const stocktakeCreateSchema = z.object({
  notes: z.string().trim().max(500).optional().nullable(),
});

export const stocktakeLineUpdateSchema = z.object({
  actualQuantity: z.coerce.number().int().min(0).max(1_000_000),
  note: z.string().trim().max(500).optional().nullable(),
});

export const assetImportSchema = z.object({
  dryRun: z.boolean().default(false),
  mode: z.enum(["create_only", "upsert"]).default("create_only"),
  rows: z.array(z.record(z.string(), z.unknown())).min(1).max(200),
});

export const disposalCreateSchema = z.object({
  assetId: id,
  reason: z.string().trim().min(2, "请填写处置原因").max(1000),
  method: z.enum(["sold", "recycled", "scrapped", "other"]).default("recycled"),
});

export const disposalDecisionSchema = z.object({
  decision: z.enum(["approve", "reject"]),
  note: z.string().trim().max(500).optional().nullable(),
});

export const stocktakeDecisionSchema = z.object({
  decision: z.enum(["approve", "reject"]),
  note: z.string().trim().max(500).optional().nullable(),
});
